import React, { useEffect, useMemo, useState } from 'react';
import { getActiveTheme, applyThemeVars, SeasonalTheme } from '../../config/seasonalThemes.ts';

// Import all theme CSS files
import '../../styles/themes/valentine.css';

/**
 * 🎨 SeasonalThemeProvider
 * 
 * Drop this component once at the top of your app.
 * It automatically detects the current seasonal theme,
 * applies CSS classes/variables, and renders floating particles.
 * 
 * When no theme is active it renders nothing and applies no styles.
 */
const SeasonalThemeProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<SeasonalTheme | null>(null);

  useEffect(() => {
    // Check theme on mount
    const active = getActiveTheme();
    setTheme(active);
    applyThemeVars(active);

    // Re-check every hour (in case app stays open past midnight)
    const interval = setInterval(() => {
      const current = getActiveTheme();
      setTheme((prev: SeasonalTheme | null) => {
        if (prev?.id !== current?.id) {
          applyThemeVars(current);
          return current;
        }
        return prev;
      });
    }, 60 * 60 * 1000);

    return () => {
      clearInterval(interval);
      applyThemeVars(null); // Clean up on unmount
    };
  }, []);

  return (
    <>
      {children}
      {theme?.particles?.enabled && (
        <FloatingParticles theme={theme} />
      )}
    </>
  );
};

/**
 * 💫 Floating emoji particles
 * Each particle gets randomized position, size, speed, and drift.
 */
const FloatingParticles: React.FC<{ theme: SeasonalTheme }> = React.memo(({ theme }) => {
  const particles = useMemo(() => {
    if (!theme.particles) return [];
    const { emojis, count, speed = 1 } = theme.particles;
    
    return Array.from({ length: count }, (_, i) => {
      const emoji = emojis[i % emojis.length];
      const left = Math.random() * 100;                        // % from left
      const duration = (12 + Math.random() * 18) / speed;      // 12-30s
      const delay = -(Math.random() * duration);                // stagger start
      const size = 0.8 + Math.random() * 1;                    // rem
      const drift = -30 + Math.random() * 60;                  // px horizontal sway
      const maxOpacity = 0.3 + Math.random() * 0.45;           // 0.3–0.75
      const midScale = 0.8 + Math.random() * 0.4;              // 0.8–1.2

      return {
        key: `${theme.id}-p-${i}`,
        emoji,
        style: {
          left: `${left}%`,
          '--duration': `${duration}s`,
          '--delay': `${delay}s`,
          '--size': `${size}rem`,
          '--drift': `${drift}px`,
          '--max-opacity': maxOpacity,
          '--mid-scale': midScale,
        } as React.CSSProperties,
      };
    });
  }, [theme.id, theme.particles]);

  return (
    <div className="seasonal-particles" aria-hidden="true">
      {particles.map(p => (
        <span key={p.key} className="seasonal-particle" style={p.style}>
          {p.emoji}
        </span>
      ))}
    </div>
  );
});

FloatingParticles.displayName = 'FloatingParticles';

export default SeasonalThemeProvider;
